import {ElementRef, Inject, Injectable, OnDestroy, Optional} from '@angular/core';
import {AbsoluteDropZoneDirective} from '@shared/absolute-drag/absolute-drop-zone.directive';
import {DOCUMENT} from '@angular/common';
import {coerceElement} from '@angular/cdk/coercion';
import {Point} from '@angular/cdk/drag-drop';
import {fromEvent, of, Subject} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {AbsoluteDragDirective} from '@shared/absolute-drag/absolute-drag.directive';
import {clamp, deepCloneNode, dividePoint, subtractPoints} from '@shared/absolute-drag/helpers';
import {DropZoneComponent} from '@blocks/draggable/drop-zone/drop-zone.component';
import {DraggableService} from '@blocks/draggable/draggable.service';
import {DropZone} from '@blocks/draggable/drop-zone/drop-zone';
import {EditorRoutingService} from '@management/editor/editor-routing.service';

@Injectable()
export class AbsoluteDragService implements OnDestroy {

  private dropZoneInstances: Map<number, AbsoluteDropZoneDirective> = new Map<number, AbsoluteDropZoneDirective>();
  private dropZoneBoundaries: Map<HTMLElement, DOMRect> = new Map<HTMLElement, DOMRect>();
  private selectedDropZone: DropZoneComponent;
  private lastId = 0;

  private clones: Map<number, HTMLElement> = new Map<number, HTMLElement>();
  private lastCloneId = 0;

  private cloneContainer: HTMLDivElement;

  private offsetInHandle: Point = {x: 0, y: 0};
  private handleOffset: Point = {x: 0, y: 0};
  private draggableSize: Point = {x: 0, y: 0};
  private rawPointerDragPosition = new Subject<Point>();
  public drop = new Subject<DragEvent>();

  constructor(@Inject(DOCUMENT) private document: Document,
              @Optional() private editorRoutingService: EditorRoutingService,
              @Optional() private draggableService: DraggableService) {
  }

  public init() {
    this.createCloneContainer();

    this.drop.subscribe((event: DragEvent) => {
      this.handleDrop(event);
    });
  }

  public registerDropZone(dropZoneInstance: AbsoluteDropZoneDirective) {
    const id = this.lastId++;
    this.dropZoneInstances.set(id, dropZoneInstance);
    return id;
  }

  public unregisterDropZone(id: number) {
    if (this.dropZoneInstances.has(id)) {
      this.dropZoneInstances.delete(id);
    }
  }

  public startDrag(e: ElementRef | HTMLElement, absoluteDragDirective: AbsoluteDragDirective, event: DragEvent) {
    this.showCloneContainer();

    const dragHandlePoint: Point = absoluteDragDirective.getDragHandlePoint();
    const dragElementPoint: Point = absoluteDragDirective.getDragElementPoint();
    const cursorPoint: Point = { x: event.clientX, y: event.clientY };
    this.offsetInHandle = subtractPoints(cursorPoint, dragHandlePoint);
    this.handleOffset = subtractPoints(dragHandlePoint, dragElementPoint);

    const cloneId = this.addClone(e, event);
    this.setupDragListener();
    this.startCloneTracking(cloneId)
    this.cacheDropZoneBoundaries();
    return cloneId;
  }

  public handleDrop(event: DragEvent) {
    if (!this.draggableService) {
      return;
    }

    if (!this.selectedDropZone) {
      return;
    }

    const cursorPoint: Point = {
      x: event.clientX,
      y: event.clientY
    };

    const elementPoint = this.computeElementPosition(cursorPoint, false);
    const dropZoneRect = this.selectedDropZone.elementRef.nativeElement.getBoundingClientRect();
    const dropZonePoint: Point = {
      x: dropZoneRect.x,
      y: dropZoneRect.y
    };

    // Calculate new final point. NewPoint from the stop method is relative to the top left corner of the viewport
    // So we need to subtract the point of the dropzone to bring it into relation of the dropzone
    let newFinalPoint = subtractPoints(elementPoint, dropZonePoint);

    // Scale new point
    newFinalPoint = dividePoint(newFinalPoint, this.selectedDropZone.scaleFactor);

    this.draggableService.relatedBlock = this.selectedDropZone.parent;
    this.draggableService.zone = DropZone.Inside;
    this.draggableService.position = this.selectedDropZone.parent.children.length;
    this.draggableService.absolutePosition = newFinalPoint;

    const block_id = this.draggableService.block?.id;
    if (block_id && this.editorRoutingService?.selectedBlock !== block_id) {
      setTimeout(() => {
        this.editorRoutingService?.navigateToBlock(block_id);
      });
    }

    this.draggableService.apply();

    this.reset();
  }

  public selectDropZone(dropzone: DropZoneComponent) {
    this.selectedDropZone = dropzone;

    // handle changed scaling for drag clone
    const cloneElement = this.clones.get(this.lastCloneId);
    if (cloneElement) {
      this.draggableSize = {x: cloneElement.offsetWidth * dropzone.scaleFactor, y: cloneElement.offsetHeight * dropzone.scaleFactor};
      cloneElement.style.transform = `scale(${dropzone.scaleFactor})`;
      cloneElement.style.transformOrigin = 'top left';
    }
  }

  private startCloneTracking(cloneId: number) {
    if (!this.clones.has(cloneId)) {
      throw new Error(`no clone with id '${cloneId}' found`);
    }

    const cloneElement = this.clones.get(cloneId);

    this.rawPointerDragPosition
      .pipe(
        takeUntil(this.drop)
      )
      .subscribe(position => {
        const newElementPoint = this.computeElementPosition(position);
        cloneElement.style.top = newElementPoint.y + 'px';
        cloneElement.style.left = newElementPoint.x + 'px';
      })
  }

  private computeElementPosition(pointerPosition: Point, cached = true): Point {
    // Position corrected for the offset of the pointer in the handle
    let newPoint = subtractPoints(pointerPosition, this.offsetInHandle);

    // Position corrected for the offset of the handle in relation to the draggable element itself
    newPoint = subtractPoints(newPoint, this.handleOffset);

    // drop-zone snapping
    return this.getConstrainedPointerPosition(newPoint, cached);
  }

  private setupDragListener() {
    // Listen to the ~~drag~~ dragover event from document to get the current pointer position.
    // We can't use mousemove because mousemove isn't fired when dragging an element
    // ---
    // Firefox sets any coordinate in the drag event to 0, why? I don't know because 'f*** you' I guess.
    // A Workaround is to use the dragover event. Found in this 13 years old bugreport:
    // https://bugzilla.mozilla.org/show_bug.cgi?id=505521#c8
    fromEvent<DragEvent>(this.document, 'dragover')
      .pipe(
        switchMap(event => {
          return of({
            x: event.clientX,
            y: event.clientY
          })
        }),
        takeUntil(this.drop)
      )
      .subscribe(point => this.rawPointerDragPosition.next(point))
  }

  /**
   *
   * @param e the element to clone.
   */
  public addClone(e: ElementRef | HTMLElement, event: DragEvent): number {
    if (!e) {
      throw new Error('need element to be cloned')
    }

    const id = ++this.lastCloneId;
    const element = coerceElement<HTMLElement>(e);
    const clone = deepCloneNode(element);
    const closestPdfPartChild = element.closest('pl-pdf-part-child') as HTMLElement;

    const appBlockElement = (clone.querySelector('app-block') as HTMLElement);
    if (appBlockElement) {
      appBlockElement.style.height = '100%';
    }

    const appTextInputElement = (clone.querySelector('app-text-input') as HTMLElement);
    if (appTextInputElement) {
      appTextInputElement.style.height = '100%';
    }

    const formGroupElement = (clone.querySelector('.form-group') as HTMLElement);
    if (formGroupElement) {
      formGroupElement.style.height = '100%';
    }

    const textAreaElement = (clone.querySelector('textarea') as HTMLElement);
    if (textAreaElement) {
      textAreaElement.style.height = '100%';
      textAreaElement.style.resize = 'none';
    }

    clone.style.position = 'absolute';
    clone.style.width = closestPdfPartChild.offsetWidth + 'px';
    clone.style.height = closestPdfPartChild.offsetHeight + 'px';
    this.draggableSize = {x: element.offsetWidth, y: element.offsetHeight};

    clone.style.setProperty('--dynamic-border-size', closestPdfPartChild.style.getPropertyValue('--dynamic-border-size'));

    const scaleParent = element.closest('.scale') as HTMLElement;
    if (scaleParent) {
      clone.style.transform = scaleParent.style.transform;
      clone.style.transformOrigin = 'top left';
    }
    if (event) {
      const position = {
        x: event.clientX,
        y: event.clientY
      };
      const newElementPoint = this.computeElementPosition(position);
      clone.style.top = newElementPoint.y + 'px';
      clone.style.left = newElementPoint.x + 'px';
    }

    this.clones.set(id, clone);
    this.cloneContainer.appendChild(clone);
    return id;
  }

  public reset() {
    this.cloneContainer.childNodes.forEach(cn => cn.remove());
    this.offsetInHandle = {x: 0, y: 0};
    this.handleOffset = {x: 0, y: 0};
    this.draggableSize = {x: 0, y: 0};
    this.hideCloneContainer();
  }

  // Removes the clone-container when the angular cli refreshes the page.
  ngOnDestroy() {
    this.removeCloneContainer();
  }

  private cacheDropZoneBoundaries() {
    this.dropZoneBoundaries.clear();
    for (const dropZone of this.dropZoneInstances.values()) {
      const el = coerceElement(dropZone.elementRef);
      this.dropZoneBoundaries.set(el, el?.getBoundingClientRect());
    }
  }

  private createCloneContainer() {
    if (this.cloneContainer != null) {
      throw new Error('AbsoluteDragService can only be initializen once.');
    }

    this.cloneContainer = this.document.createElement('div');
    this.cloneContainer.classList.add('drag-clone-container');
    this.cloneContainer.style.position = 'absolute';
    this.cloneContainer.style.zIndex = '9999';
    this.cloneContainer.style.pointerEvents = 'none';
    this.cloneContainer.style.inset = '0';

    this.document.body.appendChild(this.cloneContainer);
  }

  private removeCloneContainer() {
    this.cloneContainer?.remove();
  }

  private hideCloneContainer() {
    this.cloneContainer.style.display = 'none';
  }

  private showCloneContainer() {
    this.cloneContainer.style.display = 'block';
  }

  // Gets the pointer position on the page, accounting for any position constraints.
  private getConstrainedPointerPosition(point: Point, cached = true): Point {
    let {x, y} = point;

    let boundaryRect;

    if (cached) {
      boundaryRect = this.dropZoneBoundaries.get(this.selectedDropZone?.elementRef?.nativeElement)
    } else {
      boundaryRect = this.selectedDropZone?.elementRef?.nativeElement?.getBoundingClientRect();
    }

    if (boundaryRect) {
      const minY = boundaryRect.top;
      const maxY = boundaryRect.bottom - (this.draggableSize.y);
      const minX = boundaryRect.left;
      const maxX = boundaryRect.right - (this.draggableSize.x);

      x = clamp(x, minX, maxX);
      y = clamp(y, minY, maxY);
    }

    return {x, y};
  }
}
